import React, { useState } from "react";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  COLUMN_SPAN_OPTIONS,
  GRID_COLUMNS,
  MIN_COLUMN_SPAN,
} from "../../../constants/org-dashboard";

export interface ColumnMenuProps {
  index: number;
  width: number;
  /** Width the neighbouring column could give up, so we only offer valid sizes. */
  neighbourWidth: number;
  columnCount: number;
  onResize: (index: number, width: number) => void;
  onSplit: (index: number) => void;
  onAddColumn: () => void;
  onMoveColumn: (index: number, offset: -1 | 1) => void;
  onRemoveColumn: (index: number) => void;
}

const ColumnMenu = ({
  index,
  width,
  neighbourWidth,
  columnCount,
  onResize,
  onSplit,
  onAddColumn,
  onMoveColumn,
  onRemoveColumn,
}: ColumnMenuProps) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const close = () => setAnchor(null);
  const run = (action: () => void) => () => {
    action();
    close();
  };

  const slack = Math.max(0, neighbourWidth - MIN_COLUMN_SPAN);
  const widths = COLUMN_SPAN_OPTIONS.filter(
    (option) => option >= MIN_COLUMN_SPAN && option <= width + slack,
  );

  return (
    <>
      <Tooltip title={`Column ${index + 1} layout options`}>
        <IconButton
          size="small"
          onClick={(event) => setAnchor(event.currentTarget)}
          aria-label={`Column ${index + 1} layout options`}
          data-testid={`dashboard-column-menu-${index}`}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
        <Box sx={{ px: 2, pt: 1, pb: 1.5 }}>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block", mb: 0.75 }}
          >
            {`Width — ${width} of ${GRID_COLUMNS}`}
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={width}
            onChange={(_event, next) => {
              if (next) onResize(index, Number(next));
            }}
            aria-label="Column width"
          >
            {widths.map((option) => (
              <ToggleButton key={option} value={option} sx={{ px: 1.25 }}>
                {option}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Divider />
        <MenuItem
          disabled={width < MIN_COLUMN_SPAN * 2}
          onClick={run(() => onSplit(index))}
        >
          <ListItemText>Split a row in two</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={columnCount >= 4}
          onClick={run(() => onAddColumn())}
        >
          <ListItemText>Add a column</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={index === 0}
          onClick={run(() => onMoveColumn(index, -1))}
        >
          <ListItemText>Move column left</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={index >= columnCount - 1}
          onClick={run(() => onMoveColumn(index, 1))}
        >
          <ListItemText>Move column right</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          disabled={columnCount <= 1}
          onClick={run(() => onRemoveColumn(index))}
        >
          <ListItemText sx={{ color: "error.main" }}>
            Remove column
          </ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default ColumnMenu;
